import { Logger } from '@/utils/logger';
import SimpleReactValidator from '@crystaldelta/simple-react-validator';
import { HttpStatus } from '@montugroup/apis-styleguide';
import { UserPasswordSchema } from '@montugroup/circuit-api-contracts';
import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import { UserService } from '@montugroup/circuit-shared/services/user.service';
import type { PharmacistLoginFormData } from '@montugroup/circuit-shared/types/pharmacist';
import { isMontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { toast } from '@montugroup/design-system';
import { TsRestResponseError, isErrorResponse } from '@ts-rest/core';
import type { ChangeEvent, FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const logger = new Logger('usePharmacistSetPassword');

const client = getTsApiV1Client();

function usePharmacistSetPassword(initialState: PharmacistLoginFormData, onBoardKey: string) {
  const validator = new SimpleReactValidator();
  const navigate = useNavigate();
  const { mutateAsync, isPending, contractEndpoint } = client.user.registerPassword.useMutation();
  const [data, setData] = useState<PharmacistLoginFormData & { rePassword?: string }>({
    ...initialState,
    rePassword: '',
  });
  const [errors, setErrors] = useState({
    email: validator.message('email', data.email.trim(), 'required|email'),
    password: validator.message('password', data.password, 'required'),
    rePassword: validator.message('rePassword', data.rePassword, `required|in:${data.password}`, {
      messages: { in: 'Passwords do not match' },
    }),
  });

  useEffect(() => {
    (async () => {
      if (onBoardKey) {
        try {
          const { data } = await UserService.getUserOnboardEmail(onBoardKey);
          if (!data) {
            toast.warn('Invalid key. Please try again!');
            navigate('/login');
            return;
          }
          if (data.expired) {
            toast.warn('The link has expired. Please reset your password.');
            navigate('/forgot-password');
            return;
          }
          setData((prev) => ({ ...prev, email: data.email }));
        } catch (e) {
          logger.error('Unexpected error with pharmacist onboard key', e);
          toast.error('Unexpected error. Please retry later.');
          navigate('/login');
        }
      }
    })();
  }, [onBoardKey, navigate]);

  const handleUserInput = (prop: string) => (e: ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: prop === 'checkbox' ? e.target.checked : e.target.value });
  };

  const validateFields = (): boolean => {
    const validationErrors = {
      email: validator.message('email', data.email.trim(), 'required|email'),
      password: UserPasswordSchema.safeParse(data.password).error?.issues[0].message,
      rePassword: data.password !== data.rePassword ? 'Passwords do not match' : undefined,
    };
    setErrors(validationErrors);
    return Object.values(validationErrors).every((err) => !err);
  };

  const onRegister = async (formData: PharmacistLoginFormData, e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!validateFields()) {
      validator.showMessages();
      return;
    }

    try {
      const response = await mutateAsync({
        body: {
          email: formData.email,
          token: onBoardKey,
          password: formData.password,
        },
        query: {},
      });
      if (response.status === HttpStatus.OK) {
        setData({ email: '', password: '', rePassword: '' });
        toast.success('Registered Successfully. Please Login now.', {});
        navigate('/login', { replace: true });
      } else {
        throw new TsRestResponseError(contractEndpoint, response);
      }
    } catch (error) {
      if (isErrorResponse(error, contractEndpoint) && isMontuError(error)) {
        if (error.body.error.code === 'password.requirements_not_met') {
          toast.error(
            'The password you entered is invalid as it does not meet our security requirements. Please choose a different password.',
          );
        } else if (error.body.error.message) {
          toast.error(error.body.error.message);
        }
      } else {
        toast.error('Error Occurred! Try again later!');
      }

      logger.error('handleRegister', error);
    }
  };

  return {
    data,
    errors,
    setErrors,
    handleUserInput,
    onRegister,
    isPending,
    validator,
  };
}

export default usePharmacistSetPassword;
